/**
 * Dialog for requesting a procedure (neuter, x-ray, ultrasound, sedation) for a patient.
 * The fee preview reads the server-computed procedure fees from the subscription query; the
 * server recomputes the fee when the request is created.
 */
import { useSelector } from '@tanstack/react-form'
import { useId } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import {
  formatRegionPrice,
  HEALTHY_SUBSCRIPTION_STATUSES,
  type PatientWithOwners,
  PROCEDURE_TYPES,
  type ProcedureType,
  procedureTypeSchema,
} from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { useCreateTask } from '@/frontend/features/tasks/use-create-task'
import { useGetPatientSubscription } from '@/frontend/hooks/api/patients/use-get-patient-subscription'

const requestProcedureSchema = z.object({
  procedure: procedureTypeSchema,
  note: z.string().trim().min(1, 'A note for the team is required').max(1000),
  urgent: z.boolean(),
})

type RequestProcedureValues = z.input<typeof requestProcedureSchema>

// Dental has its own dialog and request flow, so it stays out of the picker.
const SELECTABLE_PROCEDURE_TYPES = PROCEDURE_TYPES.filter(({ value }) => value !== 'dental')

interface RequestProcedureDialogProps {
  patient: PatientWithOwners
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RequestProcedureDialog({
  patient,
  open,
  onOpenChange,
}: RequestProcedureDialogProps) {
  const { mutateAsync: createTask } = useCreateTask()
  const subscriptionQuery = useGetPatientSubscription(patient.id)
  const urgentId = useId()

  const form = useAppForm({
    defaultValues: {
      procedure: 'neuter',
      note: '',
      urgent: false,
    } as RequestProcedureValues,
    validators: { onSubmit: requestProcedureSchema },
    onSubmit: async ({ value, formApi }) => {
      const { procedure, note, urgent } = requestProcedureSchema.parse(value)
      try {
        const { task } = await createTask({
          patientId: patient.id,
          data: { type: 'procedure_request', procedure, note, urgent },
        })
        const isFeeRequest = task?.data.type === 'procedure_request' && task.data.fee
        const awaitingPayment = isFeeRequest && task.status === 'open'
        toast.success(
          awaitingPayment
            ? `Payment link sent to ${patient.owner.email}`
            : isFeeRequest
              ? "Fee covered by the member's credit balance. Sent to the team"
              : 'Sent to the team'
        )
        form.reset()
        onOpenChange(false)
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to send procedure request',
            fields: {},
          },
        })
      }
    },
  })
  const procedure = useSelector(form.store, (s) => s.values.procedure) as ProcedureType

  const { data: subscription } = subscriptionQuery
  // Sending is blocked until the membership check has settled: the server would reject the
  // request on the same conditions, and the fee preview would be wrong.
  const eligible =
    subscriptionQuery.isSuccess &&
    !!subscription?.status &&
    HEALTHY_SUBSCRIPTION_STATUSES.has(subscription.status)

  /** One line under the note telling the vet what sending will do. */
  const feePreview = () => {
    if (subscriptionQuery.isPending) return 'Checking fee…'
    if (subscriptionQuery.isError || !subscription)
      return 'Couldn’t check membership fees. Please try again.'
    if (subscription.stripeSubscriptionId === null)
      return 'This patient has no membership, so a procedure cannot be requested.'
    // The stored subscription ID remains available when Stripe details cannot be resolved.
    if (!subscription.status)
      return 'Couldn’t resolve this patient’s subscription in Stripe. Check the subscription link or try again.'
    if (!HEALTHY_SUBSCRIPTION_STATUSES.has(subscription.status))
      return "This patient's membership is not active, so a procedure cannot be requested."
    const fee = subscription.procedureFees.find((f) => f.procedure === procedure)
    if (fee) {
      return `Fee ${formatRegionPrice(fee.amount, patient.owner.region)}. A payment link will be emailed to ${patient.owner.email}.`
    }
    return 'No fee. The request goes straight to the team.'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request procedure — {patient.name}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
        >
          <form.AppForm>
            <div className="flex flex-col gap-4 py-2">
              <form.GlobalError />

              <form.AppField name="procedure">
                {(field) => (
                  <field.SelectField label="Procedure" items={SELECTABLE_PROCEDURE_TYPES} />
                )}
              </form.AppField>

              <form.AppField name="note">
                {(field) => (
                  <field.TextareaField
                    label="Note"
                    required
                    placeholder="e.g. Owner keen to book, no recent bloods…"
                    maxLength={1000}
                    autoFocus
                  />
                )}
              </form.AppField>

              <form.Field name="urgent">
                {(field) => (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={urgentId}
                      checked={field.state.value}
                      onCheckedChange={(checked) => field.handleChange(checked === true)}
                    />
                    <label htmlFor={urgentId} className="font-medium cursor-pointer">
                      Urgent
                    </label>
                  </div>
                )}
              </form.Field>

              <p className="text-sm text-muted-foreground">{feePreview()}</p>
            </div>

            <form.Subscribe selector={(s) => [s.isPristine, s.isSubmitting] as const}>
              {([isPristine, isSubmitting]) => (
                <div className="flex items-center justify-end gap-2 pt-3">
                  <Button
                    variant="outline"
                    size="lg"
                    type="button"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <form.SubmitButton disabled={isPristine || !eligible}>
                    Send request
                  </form.SubmitButton>
                </div>
              )}
            </form.Subscribe>
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  )
}
